import React, { useState } from "react";
import { 
  AppBar, 
  Toolbar, 
  Box, 
  IconButton, 
  Badge, 
  Tooltip, 
  Button, 
  Popover, 
  Grid, 
  Menu, 
  MenuItem, 
  useMediaQuery,
  styled 
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { MdMenu, MdPerson, MdLogout, MdCalendarToday, MdExpandMore } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Logo from "../image/logo.png";
import MarkAttendanceModal from "../markAttendance/markAttendanceModal ";
import { NotificationList } from "../notification/Notification";
import { useIdleTimer } from "react-idle-timer";
import { hasAccessToModule } from "../rbac/hasAccess";

// modern look
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backdropFilter: "blur(20px)",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  color: "#1e293b",
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: "70px !important",
  justifyContent: "space-between",
  padding: "0 24px !important",
});

const RoleButton = styled(Button)(({ theme }) => ({
  backgroundColor: "rgba(241, 245, 249, 0.8)",
  color: "#334155",
  borderRadius: "10px",
  padding: "6px 16px",
  textTransform: "uppercase",
  fontWeight: 700,
  fontSize: "0.75rem",
  letterSpacing: "0.025em",
  border: "1px solid rgba(226, 232, 240, 0.8)",
  marginLeft: "16px",
  "&:hover": {
    backgroundColor: "rgba(226, 232, 240, 1)",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    border: "none",
    color: "#64748b",
  }
}));

const ProfileButton = styled(Button)(({ theme }) => ({
  backgroundColor: "rgba(241, 245, 249, 0.8)",
  color: "#334155",
  borderRadius: "10px",
  padding: "8px 16px",
  marginLeft: "12px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  border: "1px solid rgba(226, 232, 240, 0.8)",
  "&:hover": {
    backgroundColor: "rgba(226, 232, 240, 1)",
  },
}));

const TopNavbar = ({ rolesInfo, username, userTypeLabel, setIsLoggedIn, setSidebarOpen, isSidebarOpen }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [notificationsNo, setNotificationsNo] = React.useState(0);
  
  //  separate anchors
  const [anchorProfile, setAnchorProfile] = useState(null);
  const [anchorNotification, setAnchorNotification] = useState(null);
  const [anchorRole, setAnchorRole] = useState(null);

  const openProfile = Boolean(anchorProfile);
  const openNotification = Boolean(anchorNotification);
  const openRole = Boolean(anchorRole);

  // ---- PROFILE ----
  const handleProfileOpen = (event) => setAnchorProfile(event.currentTarget);
  const handleProfileClose = () => setAnchorProfile(null);

  // ---- NOTIFICATION --
  const handleNotificationClick = (event) => setAnchorNotification(event.currentTarget);
  const handleNotificationClose = () => setAnchorNotification(null);
  
  // ---- ROLE MENU ----
  const handleRoleClick = (event) => {
    if (rolesInfo.roles && rolesInfo.roles?.length > 1) {
      setAnchorRole(event.currentTarget);
    }
  };
  const handleRoleClose = () => setAnchorRole(null);

  // ---- LOGOUT ----
  const handleLogout = async () => {
  const savedUser = localStorage.getItem("rememberUserName");
  const savedPass = localStorage.getItem("rememberPassword");
  const savedRememberMe = localStorage.getItem("rememberMe");

  //  CLEAR EVERYTHING
    sessionStorage.clear();
    localStorage.clear();  

  //  RESTORE THE REMEMBER ME CREDENTIALS
  if (savedUser) localStorage.setItem("rememberUserName", savedUser);
  if (savedPass) localStorage.setItem("rememberPassword", savedPass);
  if (savedRememberMe) localStorage.setItem("rememberMe", savedRememberMe);  
    
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
    
    setIsLoggedIn(null);
    window.location.reload(true);
  };

  // auto log-out
  const onIdle = () => {
    handleLogout();
  };

  const onActive = () => { };

  const onAction = () => { };

  useIdleTimer({
    onIdle,
    onActive,
    onAction,
    timeout: 15 * 60 * 1000, // 15 minutes
    throttle: 250,
    crossTab: true,
  });

  return (
    <>
      <StyledAppBar position="sticky">
        <StyledToolbar>
          {/* Left Side Hamburger, Logo & Roles */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              sx={{ 
                ml: -1,
                mr: 1, 
                backgroundColor: "rgba(241, 245, 249, 0.8)",
                border: "1px solid rgba(186, 230, 253, 0.4)",
                borderRadius: "10px",
                padding: "8px",
                "&:hover": { backgroundColor: "rgba(226, 232, 240, 1)" }
              }}
            >
              <MdMenu size={22} />
            </IconButton>

            {!isMobile && (
              <Box
                onClick={() => navigate("/dashboard")}
                sx={{
                  height: "44px",
                  maxWidth: "160px",
                  minWidth: "44px",
                  marginRight: "20px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={`${Logo}`}
                  alt="logo"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                    objectPosition: "left center",
                  }}
                />
              </Box>
            )}

            {!isMobile && (
              <Tooltip title="Switch Role">
                <span>
                  {rolesInfo.roles?.length ? (
                    <RoleButton
                      onClick={handleRoleClick}
                      disabled={rolesInfo.roles?.length === 1}
                      endIcon={rolesInfo.roles?.length > 1 ? <MdExpandMore /> : null}
                    >
                      {rolesInfo.currentRole?.name || "Select Role"}
                    </RoleButton>
                  ) : null}
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Right Side Notification & Profile */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleNotificationClick}
                sx={{ 
                  backgroundColor: "rgba(241, 245, 249, 0.8)",
                  borderRadius: "10px",
                  padding: "8px",
                  height: "40px",
                  width: "40px",
                  "&:hover": { backgroundColor: "rgba(226, 232, 240, 1)" }
                }}
              >
                <Badge color="error" badgeContent={notificationsNo}>
                  <NotificationsNoneIcon sx={{ color: "#334155" }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <ProfileButton
              onClick={handleProfileOpen}
              startIcon={<MdPerson size={20} />}
              endIcon={<MdExpandMore />}
            >
              {!isMobile && (localStorage.getItem("employeesName") ? `${username} (${localStorage.getItem("employeesName")})` : username)}
            </ProfileButton>
          </Box>
        </StyledToolbar>
      </StyledAppBar>

      {/* Profile Menu */}
      <Popover
        open={openProfile}
        anchorEl={anchorProfile}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            padding: "8px",
            borderRadius: "12px",
            minWidth: "200px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
          }
        }}
      >
        <Grid container direction="column" spacing={0.5}>
          <Grid item>
            {hasAccessToModule(6) && <MarkAttendanceModal />}
          </Grid>
          <Grid item>
            <Button
              fullWidth
              sx={{ justifyContent: "flex-start", px: 2, py: 1, color: "#334155" }}
              onClick={() => {
                navigate(`/master/employeeMasterPrint/${localStorage.getItem("employeeId")}`);
                handleProfileClose();
              }}
            >
              <MdPerson size={18} style={{ marginRight: "12px" }} />
              Profile
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              sx={{ justifyContent: "flex-start", px: 2, py: 1, color: "#334155" }}
              onClick={() => {
                navigate(`/master/employeeCalendar/${localStorage.getItem("employeeId")}`);
                handleProfileClose();
              }}
            >
              <MdCalendarToday size={18} style={{ marginRight: "12px" }} />
              Calendar
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              sx={{ justifyContent: "flex-start", px: 2, py: 1, color: "#ef4444" }}
              onClick={handleLogout}
            >
              <MdLogout size={18} style={{ marginRight: "12px" }} />
              Logout
            </Button>
          </Grid>  
          
          {isMobile && rolesInfo.roles?.length > 1 && (
            <Grid item>
              <Button
                fullWidth
                sx={{ justifyContent: "flex-start", px: 2, py: 1, color: "#334155", fontWeight: 600 }}
                onClick={handleRoleClick}
              >
                Role: {rolesInfo.currentRole?.name}
              </Button>
            </Grid>        
          )}
        </Grid>
      </Popover>

      {/* Role Menu */}
      <Menu
        anchorEl={anchorRole}
        open={openRole}
        onClose={handleRoleClose}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            minWidth: "180px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
          }
        }}
      >
        {(rolesInfo?.roles || []).map((role) => (
          <MenuItem 
            key={role.id} 
            onClick={() => {
              rolesInfo.setCurrentRole(role);
              handleRoleClose();
            }}
            selected={rolesInfo.currentRole?.id === role.id}
            sx={{ px: 2, py: 1.5, fontSize: "0.875rem" }}
          >
            {role.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Notification list */}
      <NotificationList
        anchorEl={anchorNotification}
        open={openNotification}
        close={handleNotificationClose}
        notifyNumber={(n) => setNotificationsNo(n)}
      />
    </>
  );
};

export default TopNavbar;
